// src/features/settings/components/registration-codes-panel/RegistrationCodesGrid.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

export default function RegistrationCodesGrid({ registrationCodes, loading, canUpdate, canDelete, onEdit, onDelete }) {
  const { t } = useTranslation(['settings', 'common']);

  const columns = useMemo(() => [
    {
      field: 'code',
      headerName: t('settings:registration_codes.grid.code'),
      width: 80,
    },
    {
      field: 'title',
      headerName: t('settings:registration_codes.grid.title'),
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'registration_category',
      headerName: t('settings:registration_codes.grid.registration_category'),
      width: 200,
      valueFormatter: (value) => (value ? t(`settings:registration_codes.enums.registration_category.${value}`, { defaultValue: value }) : ''),
    },
    {
      field: 'work_time_type',
      headerName: t('settings:registration_codes.grid.work_time_type'),
      width: 150,
      valueFormatter: (value) => (value ? t(`settings:registration_codes.enums.work_time_type.${value}`, { defaultValue: value }) : ''),
    },
    {
      field: 'account_handling',
      headerName: t('settings:registration_codes.grid.account_handling'),
      width: 200,
      valueFormatter: (value) => (value ? t(`settings:registration_codes.enums.account_handling.${value}`, { defaultValue: value }) : ''),
    },
    {
      field: 'is_registration_code',
      headerName: t('settings:registration_codes.grid.is_registration_code'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common:yes') : t('common:no')}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'is_time_detail_code',
      headerName: t('settings:registration_codes.grid.is_time_detail_code'),
      width: 105,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common:yes') : t('common:no')}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'required_seniority_months',
      headerName: t('settings:registration_codes.grid.required_seniority_months'),
      width: 130,
      renderCell: (params) => params.value > 0 ? params.value : '-',
    },
    {
      field: 'actions',
      renderHeader: () => (<></>),
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          {canUpdate && (
            <IconButton
              size="small"
              onClick={() => onEdit(params.row)}
              title={t('settings:registration_codes.buttons.edit')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              onClick={() => onDelete(params.row)}
              title={t('common:buttons.delete')}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ], [t, canUpdate, canDelete, onEdit, onDelete]);

  return (
    <DataGrid
      rows={registrationCodes}
      columns={columns}
      getRowId={(row) => row.registration_code_id}
      loading={loading}
      pageSizeOptions={[10, 25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      disableColumnFilter
      disableColumnSorting
      disableColumnMenu
      disableColumnResize
      disableRowSelectionOnClick
      sx={{ minHeight: 400 }}
    />
  );
}
